import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Check, ChevronDown, ChevronUp, Pencil, Plus, Trash2, X } from 'lucide-react';
import { api, money } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { CentsInput, Switch } from './bits';
import { byRank, errMsg, type PizzaBase, type PizzaSize, type PizzaSizeRequest, type PizzaTopping, type PizzaToppingRequest, type ToppingPrice } from './types';
import './menu.css';

type SizeDraft = { name: string; halfAndHalfEnabled: boolean; halfAndHalfExtraChargeInCents: number; bases: Record<string, number> };
type BaseDraft = { name: string; docketLabel: string };
type ToppingDraft = { name: string; prices: Record<string, number> };

const freshSize = (): SizeDraft => ({ name: '', halfAndHalfEnabled: true, halfAndHalfExtraChargeInCents: 0, bases: {} });
const freshBase = (): BaseDraft => ({ name: '', docketLabel: '' });
const freshTopping = (): ToppingDraft => ({ name: '', prices: {} });

/** Bases, sizes & toppings — the shared vocabulary pizza items build on.
 * Every write returns the one entity; the lists reload after each action. */
export function PizzaSettings() {
  const { rid, sid } = useScope();
  const base = useBase();

  const [bases, setBases] = useState<PizzaBase[] | null>(null);
  const [sizes, setSizes] = useState<PizzaSize[] | null>(null);
  const [toppings, setToppings] = useState<PizzaTopping[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editBaseId, setEditBaseId] = useState<string | null>(null);
  const [baseDraft, setBaseDraft] = useState<BaseDraft>(freshBase());
  const [newBase, setNewBase] = useState<BaseDraft>(freshBase());
  const [editSizeId, setEditSizeId] = useState<string | null>(null);
  const [sizeDraft, setSizeDraft] = useState<SizeDraft>(freshSize());
  const [newSize, setNewSize] = useState<SizeDraft>(freshSize());
  const [editTopId, setEditTopId] = useState<string | null>(null);
  const [topDraft, setTopDraft] = useState<ToppingDraft>(freshTopping());
  const [newTop, setNewTop] = useState<ToppingDraft>(freshTopping());

  const load = useCallback(async () => {
    try {
      const [b, s, t] = await Promise.all([
        api.get<PizzaBase[]>(`${base}/pizza-bases`),
        api.get<PizzaSize[]>(`${base}/pizza-sizes`),
        api.get<PizzaTopping[]>(`${base}/pizza-toppings`),
      ]);
      setBases(b.sort(byRank));
      setSizes(s.sort(byRank));
      setToppings(t.sort(byRank));
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'Failed to load pizza settings.'));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await load();
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'That didn’t work.'));
    } finally {
      setBusy(false);
    }
  }

  const confirmDelete = (what: string, fn: () => Promise<unknown>) => {
    if (confirm(`Delete ${what}?`)) void act(fn);
  };

  // ── bases ─────────────────────────────────────────────────────────

  const baseBody = (d: BaseDraft) => ({ name: d.name.trim(), docketLabel: d.docketLabel.trim() || null });
  const baseForm = (d: BaseDraft, onChange: (n: BaseDraft) => void, idPrefix: string) => (
    <>
      <input className="input" style={{ width: 160 }} placeholder="Base name…" aria-label={`${idPrefix} base name`} value={d.name}
        onChange={(e) => onChange({ ...d, name: e.target.value })} />
      <input className="input" style={{ width: 110 }} placeholder="Docket label" aria-label={`${idPrefix} docket label`} value={d.docketLabel}
        onChange={(e) => onChange({ ...d, docketLabel: e.target.value })} />
    </>
  );

  // ── sizes ─────────────────────────────────────────────────────────

  const sizeBody = (d: SizeDraft): PizzaSizeRequest => ({
    name: d.name.trim(),
    halfAndHalfEnabled: d.halfAndHalfEnabled,
    halfAndHalfExtraChargeInCents: d.halfAndHalfExtraChargeInCents,
    bases: (bases ?? []).filter((b) => d.bases[b.baseId] !== undefined).map((b) => ({ baseId: b.baseId, extraChargeInCents: d.bases[b.baseId] })),
  });

  const sizeForm = (d: SizeDraft, onChange: (n: SizeDraft) => void, idPrefix: string) => (
    <>
      <input className="input" style={{ width: 160 }} placeholder="Size name…" aria-label={`${idPrefix} size name`} value={d.name}
        onChange={(e) => onChange({ ...d, name: e.target.value })} />
      <label className="mn-check" title="Allow half & half splits at this size">
        <input type="checkbox" checked={d.halfAndHalfEnabled} onChange={(e) => onChange({ ...d, halfAndHalfEnabled: e.target.checked })} />
        half & half
      </label>
      {d.halfAndHalfEnabled && (
        <>
          <span className="mn-hint" style={{ whiteSpace: 'nowrap' }}>+ split charge</span>
          <CentsInput value={d.halfAndHalfExtraChargeInCents} title="Split charge" onChange={(c) => onChange({ ...d, halfAndHalfExtraChargeInCents: c })} />
        </>
      )}
      {(bases ?? []).length > 0 && (
        <span className="mn-size-extras" style={{ flexBasis: '100%' }}>
          <span className="lbl">bases:</span>
          {(bases ?? []).map((b) => {
            const on = d.bases[b.baseId] !== undefined;
            return (
              <span key={b.baseId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <label className="mn-check">
                  <input type="checkbox" checked={on}
                    onChange={(e) => {
                      const next = { ...d.bases };
                      if (e.target.checked) next[b.baseId] = 0;
                      else delete next[b.baseId];
                      onChange({ ...d, bases: next });
                    }} />
                  {b.name}
                </label>
                {on && <CentsInput value={d.bases[b.baseId]} title={`${b.name} extra`} onChange={(c) => onChange({ ...d, bases: { ...d.bases, [b.baseId]: c } })} />}
              </span>
            );
          })}
        </span>
      )}
    </>
  );

  const sizeToDraft = (s: PizzaSize): SizeDraft => ({
    name: s.name,
    halfAndHalfEnabled: s.halfAndHalfEnabled,
    halfAndHalfExtraChargeInCents: s.halfAndHalfExtraChargeInCents,
    bases: Object.fromEntries(s.bases.map((b) => [b.baseId, b.extraChargeInCents])),
  });

  // ── toppings ──────────────────────────────────────────────────────

  const toppingBody = (d: ToppingDraft, soldOut = false): PizzaToppingRequest => ({
    name: d.name.trim(),
    soldOut,
    prices: (sizes ?? [])
      .filter((s) => d.prices[s.sizeId] !== undefined)
      .map((s): ToppingPrice => ({ sizeId: s.sizeId, priceInCents: d.prices[s.sizeId] })),
  });

  const toppingForm = (d: ToppingDraft, onChange: (n: ToppingDraft) => void, idPrefix: string) => (
    <>
      <input className="input" style={{ width: 160 }} placeholder="Topping name…" aria-label={`${idPrefix} topping name`} value={d.name}
        onChange={(e) => onChange({ ...d, name: e.target.value })} />
      {(sizes ?? []).map((s) => (
        <span key={s.sizeId} className="mn-size-extras">
          <span className="lbl">{s.name}</span>
          <CentsInput value={d.prices[s.sizeId] ?? 0} title={`${s.name} price`} onChange={(c) => onChange({ ...d, prices: { ...d.prices, [s.sizeId]: c } })} />
        </span>
      ))}
    </>
  );

  const sizeName = (id: string) => sizes?.find((s) => s.sizeId === id)?.name ?? id;
  const baseName = (id: string) => bases?.find((b) => b.baseId === id)?.name ?? id;

  const mover = (path: string, idx: number, len: number) => (
    <>
      <button className="mn-iconbtn" title="Move up" disabled={busy || idx === 0} onClick={() => void act(() => api.post(`${path}/move-up`))}>
        <ChevronUp size={16} />
      </button>
      <button className="mn-iconbtn" title="Move down" disabled={busy || idx === len - 1} onClick={() => void act(() => api.post(`${path}/move-down`))}>
        <ChevronDown size={16} />
      </button>
    </>
  );

  const ready = bases !== null && sizes !== null && toppings !== null;

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Store · Menu · Pizza settings</div>
          <h1>Pizza settings</h1>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Link className="btn btn-outline btn-sm" to={`/r/${rid}/s/${sid}/menu`}>Back to menu</Link>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}
      {!ready && <div className="loading">Loading…</div>}

      {ready && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          <div className="panel" data-section="bases">
            <h2>Bases</h2>
            {bases.length === 0 && <div className="loading">No bases yet — sizes may still be sold without one.</div>}
            {bases.map((b, idx) => (
              <div key={b.baseId} className="rowline">
                {editBaseId === b.baseId ? (
                  <div className="mn-inline-form" style={{ flex: 1 }}>
                    {baseForm(baseDraft, setBaseDraft, 'Edit')}
                    <button className="btn btn-primary btn-sm" disabled={busy || !baseDraft.name.trim()}
                      onClick={() => void act(async () => { await api.put(`${base}/pizza-bases/${b.baseId}`, baseBody(baseDraft)); setEditBaseId(null); })}>
                      <Check size={14} /> Save
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditBaseId(null)}><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <div className="grow">
                      <b>{b.name}</b>
                      <span className="sub">{b.docketLabel ? `Docket: ${b.docketLabel}` : 'No docket label'}</span>
                    </div>
                    <button className="mn-iconbtn" title="Edit base" disabled={busy}
                      onClick={() => { setEditBaseId(b.baseId); setBaseDraft({ name: b.name, docketLabel: b.docketLabel ?? '' }); }}>
                      <Pencil size={14} />
                    </button>
                    {mover(`${base}/pizza-bases/${b.baseId}`, idx, bases.length)}
                    <button className="mn-iconbtn danger" title="Delete base" disabled={busy}
                      onClick={() => confirmDelete(`base “${b.name}”`, () => api.del(`${base}/pizza-bases/${b.baseId}`))}>
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            ))}
            <div className="mn-inline-form" style={{ paddingTop: 12 }}>
              {baseForm(newBase, setNewBase, 'New')}
              <button className="btn btn-primary btn-sm" disabled={busy || !newBase.name.trim()}
                onClick={() => void act(async () => { await api.post(`${base}/pizza-bases`, baseBody(newBase)); setNewBase(freshBase()); })}>
                <Plus size={14} /> Add base
              </button>
            </div>
          </div>

          <div className="panel" data-section="sizes">
            <h2>Sizes</h2>
            {sizes.length === 0 && <div className="loading">No sizes yet — pizzas need at least one.</div>}
            {sizes.map((s, idx) => (
              <div key={s.sizeId} className="rowline">
                {editSizeId === s.sizeId ? (
                  <div className="mn-inline-form" style={{ flex: 1 }}>
                    {sizeForm(sizeDraft, setSizeDraft, 'Edit')}
                    <button className="btn btn-primary btn-sm" disabled={busy || !sizeDraft.name.trim()}
                      onClick={() => void act(async () => { await api.put(`${base}/pizza-sizes/${s.sizeId}`, sizeBody(sizeDraft)); setEditSizeId(null); })}>
                      <Check size={14} /> Save
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditSizeId(null)}><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <div className="grow">
                      <b>{s.name}</b>
                      <span className="sub">
                        {s.halfAndHalfEnabled
                          ? `Half & half on${s.halfAndHalfExtraChargeInCents > 0 ? ` · +${money(s.halfAndHalfExtraChargeInCents)} split charge` : ''}`
                          : 'Half & half off'}
                        {s.bases.length > 0 && ` · bases: ${s.bases.map((b) => `${baseName(b.baseId)}${b.extraChargeInCents ? ` +${money(b.extraChargeInCents)}` : ''}`).join(', ')}`}
                      </span>
                    </div>
                    <button className="mn-iconbtn" title="Edit size" disabled={busy} onClick={() => { setEditSizeId(s.sizeId); setSizeDraft(sizeToDraft(s)); }}>
                      <Pencil size={14} />
                    </button>
                    {mover(`${base}/pizza-sizes/${s.sizeId}`, idx, sizes.length)}
                    <button className="mn-iconbtn danger" title="Delete size" disabled={busy}
                      onClick={() => confirmDelete(`size “${s.name}”`, () => api.del(`${base}/pizza-sizes/${s.sizeId}`))}>
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            ))}
            <div className="mn-inline-form" style={{ paddingTop: 12 }}>
              {sizeForm(newSize, setNewSize, 'New')}
              <button className="btn btn-primary btn-sm" disabled={busy || !newSize.name.trim()}
                onClick={() => void act(async () => { await api.post(`${base}/pizza-sizes`, sizeBody(newSize)); setNewSize(freshSize()); })}>
                <Plus size={14} /> Add size
              </button>
            </div>
          </div>

          <div className="panel" data-section="toppings" style={{ gridColumn: '1 / -1' }}>
            <h2>Toppings</h2>
            {toppings.length === 0 && <div className="loading">No toppings yet.</div>}
            {toppings.map((t, idx) => (
              <div key={t.toppingId} className="rowline">
                {editTopId === t.toppingId ? (
                  <div className="mn-inline-form" style={{ flex: 1 }}>
                    {toppingForm(topDraft, setTopDraft, 'Edit')}
                    <button className="btn btn-primary btn-sm" disabled={busy || !topDraft.name.trim()}
                      onClick={() => void act(async () => { await api.put(`${base}/pizza-toppings/${t.toppingId}`, toppingBody(topDraft, t.soldOut)); setEditTopId(null); })}>
                      <Check size={14} /> Save
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditTopId(null)}><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <div className="grow">
                      <b>{t.name}</b>
                      <span className="sub">
                        {t.prices.length === 0 ? 'No per-size prices' : t.prices.map((p) => `${sizeName(p.sizeId)} ${money(p.priceInCents)}`).join(' · ')}
                      </span>
                    </div>
                    {t.soldOut && <span className="chip chip-sold_out">sold out</span>}
                    <Switch on={!t.soldOut} disabled={busy} title={t.soldOut ? `${t.name}: sold out — tap to restore` : `${t.name}: in stock — tap to mark sold out`}
                      onToggle={() => void act(() => api.put(`${base}/pizza-toppings/${t.toppingId}/sold-out`, { soldOut: !t.soldOut }))} />
                    <button className="mn-iconbtn" title="Edit topping" disabled={busy}
                      onClick={() => { setEditTopId(t.toppingId); setTopDraft({ name: t.name, prices: Object.fromEntries(t.prices.map((p) => [p.sizeId, p.priceInCents])) }); }}>
                      <Pencil size={14} />
                    </button>
                    {mover(`${base}/pizza-toppings/${t.toppingId}`, idx, toppings.length)}
                    <button className="mn-iconbtn danger" title="Delete topping" disabled={busy}
                      onClick={() => confirmDelete(`topping “${t.name}”`, () => api.del(`${base}/pizza-toppings/${t.toppingId}`))}>
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            ))}
            {sizes.length === 0 ? (
              <div className="mn-hint">Create a size first — topping prices are per size.</div>
            ) : (
              <div className="mn-inline-form" style={{ paddingTop: 12 }}>
                {toppingForm(newTop, setNewTop, 'New')}
                <button className="btn btn-primary btn-sm" disabled={busy || !newTop.name.trim()}
                  onClick={() => void act(async () => { await api.post(`${base}/pizza-toppings`, toppingBody(newTop)); setNewTop(freshTopping()); })}>
                  <Plus size={14} /> Add topping
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
